import axios, { AxiosError } from "axios";
import { Table } from "react-bootstrap";

import { formatLedgerAmount } from "../../helpers/formatLedgerAmount";
import { formatDateByCountry } from "../../helpers/utils";
import ErrorPage from "../ErrorPage";
import PageLoader from "../PageLoader";
import {
  JournalEntryItem,
  JournalEntryResult,
  JournalEntrySortDirection,
  JournalEntrySortField,
  JournalEntryType,
} from "../../types/journalEntries";
import styles from "./index.module.scss";

type Props = {
  data?: JournalEntryResult;
  error?: Error | null;
  isLoading: boolean;
  idleMessage?: string;
  sortBy: JournalEntrySortField;
  sortDir: JournalEntrySortDirection;
  onSort: (field: JournalEntrySortField) => void;
  onReverse: (item: JournalEntryItem) => void;
  pendingTransactionId?: string;
};

const COLUMN_COUNT = 10;

const typeLabel: Record<JournalEntryType, string> = {
  CREDIT: "Credit",
  DEBIT: "Debit",
};

const formatWhen = (iso: string) =>
  formatDateByCountry(iso, "dd MMM yyyy HH:mm", "AE");

const SortHeader = ({
  label,
  field,
  sortBy,
  sortDir,
  onSort,
}: {
  label: string;
  field: JournalEntrySortField;
  sortBy: JournalEntrySortField;
  sortDir: JournalEntrySortDirection;
  onSort: (field: JournalEntrySortField) => void;
}) => {
  const active = sortBy === field;
  return (
    <th
      aria-sort={
        active ? (sortDir === "asc" ? "ascending" : "descending") : "none"
      }
    >
      <button
        type="button"
        className={styles.sortButton}
        data-testid={`journal-entries-sort-${field}`}
        onClick={() => onSort(field)}
      >
        {label}
        <span className="material-icons" style={{ fontSize: 16 }}>
          {active
            ? sortDir === "asc"
              ? "arrow_upward"
              : "arrow_downward"
            : "unfold_more"}
        </span>
      </button>
    </th>
  );
};

const TableHead = ({
  sortBy,
  sortDir,
  onSort,
}: Pick<Props, "sortBy" | "sortDir" | "onSort">) => (
  <thead>
    <tr>
      <SortHeader
        label="Posting date"
        field="entryDate"
        sortBy={sortBy}
        sortDir={sortDir}
        onSort={onSort}
      />
      <SortHeader
        label="Created date"
        field="createdAt"
        sortBy={sortBy}
        sortDir={sortDir}
        onSort={onSort}
      />
      <th>GL account</th>
      <th>Type</th>
      <th>Amount</th>
      <th>Currency</th>
      <th>Transaction ID</th>
      <th>Notes</th>
      <th>Entry ID</th>
      <th>Actions</th>
    </tr>
  </thead>
);

const TableRow = ({
  item,
  onReverse,
  pendingTransactionId,
}: {
  item: JournalEntryItem;
  onReverse: (item: JournalEntryItem) => void;
  pendingTransactionId?: string;
}) => (
  <tr data-testid="journal-entries-row">
    <td data-testid="journal-entries-posting-date">
      {formatWhen(item.entryDate)}
    </td>
    <td data-testid="journal-entries-created-date">
      {formatWhen(item.createdAt)}
    </td>
    <td data-testid="journal-entries-account">
      {item.glAccountName}
      <span className={styles.accountCode}>{item.glAccountCode}</span>
    </td>
    <td data-testid="journal-entries-type">
      {typeLabel[item.entryType] ?? item.entryType}
    </td>
    <td data-testid="journal-entries-amount">
      {formatLedgerAmount(item.amount, item.currency)}
    </td>
    <td>{item.currency}</td>
    <td>{item.transactionId}</td>
    <td>{item.notes || "—"}</td>
    <td>{item.id}</td>
    <td>
      {item.reversedByEntryId ? (
        <span data-testid="journal-entries-reversed">Reversed</span>
      ) : (
        <button
          type="button"
          className={styles.reverseButton}
          data-testid="journal-entries-reverse"
          disabled={pendingTransactionId === item.transactionId}
          onClick={() => onReverse(item)}
        >
          Reverse
        </button>
      )}
    </td>
  </tr>
);

const JournalEntriesTable = ({
  data,
  error,
  isLoading,
  idleMessage,
  sortBy,
  sortDir,
  onSort,
  onReverse,
  pendingTransactionId,
}: Props) => {
  if (isLoading) {
    return (
      <PageLoader isLoading={isLoading} content="Loading journal entries..." />
    );
  }

  if (idleMessage && !data && !error) {
    return (
      <div className={styles.table} data-testid="journal-entries-idle">
        <Table hover bordered responsive="md">
          <TableHead sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
          <tbody>
            <tr>
              <td colSpan={COLUMN_COUNT} className={styles.emptyTable}>
                {idleMessage}
              </td>
            </tr>
          </tbody>
        </Table>
      </div>
    );
  }

  if (error || !data) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<{
        errorCode: string;
        errorMessage: string;
      }>;
      return (
        <ErrorPage
          title={axiosError.response?.data?.errorCode}
          message={axiosError.response?.data?.errorMessage}
        />
      );
    }
    return <ErrorPage />;
  }

  return (
    <div className={styles.table} data-testid="journal-entries-table">
      <Table hover bordered responsive="md">
        <TableHead sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
        <tbody>
          {data.items.length > 0 ? (
            data.items.map((item) => (
              <TableRow
                key={item.id}
                item={item}
                onReverse={onReverse}
                pendingTransactionId={pendingTransactionId}
              />
            ))
          ) : (
            <tr>
              <td colSpan={COLUMN_COUNT} className={styles.emptyTable}>
                Oops, no results were found.
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
};

export default JournalEntriesTable;
